import React, { ChangeEvent, FunctionComponent, useState } from "react";
import { SolidButton, edgePaddingMinimum, fontSizes, formButton, media, zIndexes, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { Button } from "@mui/material";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";

import { NewUserQuestionnaire } from "@iventis/domain-model/model/newUserQuestionnaire";
import { NewUserQuestionnaireEventType } from "@iventis/domain-model/model/newUserQuestionnaireEventType";
import { NewUserQuestionnaireExperience } from "@iventis/domain-model/model/newUserQuestionnaireExperience";
import { NewUserQuestionnaireReason } from "@iventis/domain-model/model/newUserQuestionnaireReason";
import { StepIndicator } from "./step-indicator";
import { TrialQuestionForm } from "./trial-question-form";

export enum StepId {
    reason = "reason",
    eventType = "eventType",
    experience = "experience",
    projectName = "projectName",
}

export type SelectedStepOption = {
    step: StepId;
    answer: NewUserQuestionnaireEventType | NewUserQuestionnaireExperience | NewUserQuestionnaireReason | string;
};

export type ProgressStep = {
    label: string;
    name: string;
    id: StepId;
    content: {
        question: string;
        subLabel?: string;
        options: {
            name: string;
            id: NewUserQuestionnaireEventType | NewUserQuestionnaireExperience | NewUserQuestionnaireReason | StepId.projectName;
            icon?: string;
        }[];
    };
};

const payload: NewUserQuestionnaire = {
    reason: undefined,
    eventType: undefined,
    experience: undefined,
    projectName: "",
    userId: undefined,
    assetId: undefined,
    recommendedLayerTemplateCategoryNames: [],
};

const projectImages = {
    [NewUserQuestionnaireEventType.SportsEvent]: "2e36d79f-9fe4-41dc-b42e-64359662d0c4",
    [NewUserQuestionnaireEventType.RoadEvent]: "c2555335-c8b0-4ff1-b058-f08cd32bad6b",
    [NewUserQuestionnaireEventType.FestivalOrParade]: "1af67ec3-f317-461c-ace6-1b22ffa34cb1",
    [NewUserQuestionnaireEventType.ExhibitionOrShow]: "26ad946d-3b36-4bc3-83ab-45dc40f288e3",
    [NewUserQuestionnaireEventType.MajorEvent]: "e88e7d1a-600c-49dc-a283-1ab5e29e1d5f",
    [NewUserQuestionnaireEventType.MultipleEvents]: "03987245-943e-40b4-bd0e-8ac1c18d0fa7",
    [NewUserQuestionnaireEventType.Venue]: "d503aa55-2ce7-4663-877d-537428c2b584",
    [NewUserQuestionnaireEventType.SomethingElse]: "b1ac7205-cc04-4580-b655-5ac192c37c5f",
};

const setProjectImage = (answer?: SelectedStepOption["answer"]) => {
    payload.assetId = answer ? projectImages[answer] : "c4b2c293-6fc1-477a-b09c-25a4b9d39d20";
};

export const Questionnaire: FunctionComponent<{
    user: { firstName: string };
    onSubmit: (payload: NewUserQuestionnaire) => void;
    steps: ProgressStep[];
}> = ({ user, onSubmit, steps }) => {
    const translate = useIventisTranslate();

    const [step, setStep] = useState(0);
    const [selectedOption, setSelectedOption] = useState<SelectedStepOption>();

    const onChange = (_: ChangeEvent<HTMLInputElement>, choice: SelectedStepOption) => {
        setSelectedOption(choice);
        payload[choice.step as string] = choice.answer;
        if (choice.step === StepId.eventType) {
            setProjectImage(choice.answer);
        }
    };

    const handleNextStep = () => {
        setStep(step + 1);
    };

    const handleSkipClick = () => {
        if (steps[step]?.id === StepId.eventType) {
            setProjectImage();
            handleNextStep();
        } else {
            handleNextStep();
        }
    };

    const isLastStep = !(step < steps.length - 1);

    const disableContinue = () => {
        if (isLastStep) {
            return payload.projectName.length === 0;
        }
        return steps[step]?.id !== selectedOption?.step;
    };

    return (
        <>
            <StyledWelcomeHeader>
                <span>
                    Welcome
                    {user?.firstName != null && (
                        <>
                            , <StyledFirstName>{user?.firstName}</StyledFirstName>
                        </>
                    )}
                </span>
                {steps[step]?.id === StepId.reason && <StyledWelcomeIntro>{translate(Content.login2.questionnaire.welcomeIntro)}</StyledWelcomeIntro>}
            </StyledWelcomeHeader>
            <StyledOptionWrapper>
                <div>
                    <StepIndicidatorWrapper>
                        <StepIndicator steps={steps} startingStep={step} />
                    </StepIndicidatorWrapper>
                </div>
                <div>
                    <TrialQuestionForm step={steps[step]} onChange={onChange} selectedOption={selectedOption} />
                </div>
                <StyledButtonWrapper>
                    {!isLastStep && (
                        <StyledCancelButton disabled={steps[step]?.id === selectedOption?.step} onClick={handleSkipClick}>
                            {translate(Content.login2.questionnaire.skip)}
                        </StyledCancelButton>
                    )}

                    <SolidButton
                        style={{ alignSelf: "end", height: formButton.height, padding: "0 30px", marginLeft: "auto" }}
                        color="primary"
                        type="submit"
                        variant="contained"
                        disabled={disableContinue()}
                        onClick={isLastStep ? () => onSubmit(payload) : handleNextStep}
                        data-testid="submit-button"
                    >
                        {isLastStep ? <span>{translate(Content.login2.questionnaire.setup_completed)}</span> : <span>{translate(Content.common.buttons.continue)}</span>}
                    </SolidButton>
                </StyledButtonWrapper>
            </StyledOptionWrapper>
        </>
    );
};

const StepIndicidatorWrapper = styled.div`
    margin: 10px ${edgePaddingMinimum};
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

export const StyledFirstName = styled("span")(({ theme }) => ({
    display: "inline-block",
    color: theme.primaryColors.focus,
}));

export const StyledWelcomeHeader = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    font-size: ${fontSizes.xxLarge};
    font-weight: bold;
    justify-content: center;
    position: sticky;
    top: 115px;
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    z-index: ${zIndexes.stickyComponents};
    padding: 10px ${edgePaddingMinimum};

    ${media.medium} {
        font-size: ${fontSizes.xxxLarge};
        height: 100px;
    }
`;

export const StyledWelcomeIntro = styled.span`
    font-size: ${fontSizes.small};
    font-weight: normal;
    min-height: 36px;
    margin: 10px;

    ${media.medium} {
        font-size: ${fontSizes.xxLarge};
    }
`;

export const StyledOptionWrapper = styled.div`
    box-sizing: border-box;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
`;

const StyledCancelButton = styled(Button)`
    height: ${formButton.height};
    width: ${formButton.width};
    margin-right: 10px;
    color: ${({ theme }) => theme.typographyColors.subdued};
    :hover {
        background-color: ${({ theme }) => theme.shades.four};
    }
`;

const StyledButtonWrapper = styled("div")`
    position: sticky;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    padding: ${edgePaddingMinimum} 20px;
    ${media.medium} {
        padding: ${edgePaddingMinimum} 60px;
    }
`;
